import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Input as AntInput, Select as AntSelect, Space, theme } from 'antd';
import type { InputProps as AntInputProps, GlobalToken } from 'antd';
import styled from 'styled-components';
import type { CountryCode } from 'libphonenumber-js';
import type { InputStatus } from '../../utils/statusStyles';
import { getInputStatusStyles, getHelpTextColor } from '../../utils/statusStyles';
import { ComponentSize, getControlHeight } from '../../utils/tokenHelpers';
import { DEFAULT_COUNTRY } from './constants';
import {
  buildCountryOptions,
  formatNationalInput,
  getCountryFromValue,
  getNationalNumberFromValue,
  toE164,
} from './utils';

export interface PhoneInputProps
  extends Omit<AntInputProps, 'value' | 'onChange' | 'status' | 'size'> {
  /** Current value in E.164 format, e.g. "+33612345678" */
  value?: string;
  /** Called with the E.164-formatted number on every keystroke — may be an incomplete number */
  onChange?: (value: string) => void;
  /** Country pre-selected when the field has no value yet */
  defaultCountry?: CountryCode;
  /** Field status for validation feedback */
  status?: InputStatus;
  /** Label text */
  label?: string;
  /** Help text below the field */
  helpText?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Field size following the Figma design system */
  size?: ComponentSize;
}

const COUNTRY_OPTIONS = buildCountryOptions();

const Wrapper = styled.div<{ $token: GlobalToken }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $token }) => $token.marginXXS}px;
  width: 100%;
`;

const LabelWrapper = styled.label<{ $token: GlobalToken }>`
  display: flex;
  align-items: center;
  gap: ${({ $token }) => $token.marginXXS}px;
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSize}px;
  font-weight: 400;
  color: ${({ $token }) => $token.colorText};
`;

const RequiredMark = styled.span<{ $token: GlobalToken }>`
  color: ${({ $token }) => $token.colorError};
`;

const HelpText = styled.span<{ $token: GlobalToken; $status: InputStatus }>`
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  color: ${({ $token, $status }) => getHelpTextColor($token, $status)};
`;

interface StyledInputProps {
  $token: GlobalToken;
  $status: InputStatus;
  $height: number;
}

// Number field only — the country Select keeps its own default antd
// border/radius so the two visually join as one control (Space.Compact).
const StyledNumberInput = styled(AntInput)<StyledInputProps>`
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSize}px;
  height: ${({ $height }) => $height}px;
  ${({ $token, $status }) => getInputStatusStyles($token, $status)}
`;

const StyledCountrySelect = styled(AntSelect)<{ $height: number }>`
  &.ant-select .ant-select-selector {
    height: ${({ $height }) => $height}px;
  }
`;

/**
 * Phone number field with a country selector, formatting the number as the
 * user types and reporting it in E.164 format via `onChange`.
 *
 * Validity isn't checked internally — pair it with `isValidPhoneNumber`
 * from `libphonenumber-js` before submitting.
 *
 * @example
 * ```tsx
 * import { PhoneInput } from '@libertify/ui';
 * import { isValidPhoneNumber } from 'libphonenumber-js';
 *
 * const [phone, setPhone] = useState('');
 * <PhoneInput label="Phone number" value={phone} onChange={setPhone} defaultCountry="FR" />
 * <Button disabled={!isValidPhoneNumber(phone)}>Send code</Button>
 * ```
 */
export const PhoneInput = ({
  value = '',
  onChange,
  defaultCountry = DEFAULT_COUNTRY,
  label,
  helpText,
  required,
  status = 'default',
  size = 'm',
  disabled,
  ...inputProps
}: PhoneInputProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const { token } = theme.useToken();
  const height = getControlHeight(token, size);

  // Seeded once from `value` — the field then owns its display state, same
  // as a native <input>, rather than re-deriving it every render.
  const [country, setCountry] = useState<CountryCode>(
    () => getCountryFromValue(value) ?? defaultCountry
  );
  const [nationalInput, setNationalInput] = useState<string>(() =>
    getNationalNumberFromValue(value)
  );

  // Typed loosely here because styled(AntSelect) doesn't forward Select's
  // ValueType generic — narrowed back to CountryCode immediately, which is
  // always what COUNTRY_OPTIONS actually yields.
  const handleCountryChange = (selected: unknown) => {
    const nextCountry = selected as CountryCode;
    setCountry(nextCountry);
    onChange?.(toE164(nextCountry, nationalInput));
  };

  const handleNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value;
    setNationalInput(digits);
    onChange?.(toE164(country, digits));
  };

  return (
    <Wrapper $token={token}>
      {label && (
        <LabelWrapper $token={token}>
          {label}
          {required && <RequiredMark $token={token}>*</RequiredMark>}
        </LabelWrapper>
      )}
      <Space.Compact style={{ width: '100%' }}>
        <StyledCountrySelect
          value={country}
          onChange={handleCountryChange}
          options={COUNTRY_OPTIONS}
          disabled={disabled}
          showSearch
          optionFilterProp="label"
          style={{ width: 160, flex: 'none' }}
          $height={height}
          aria-label={t('components.phoneInput.countryLabel')}
        />
        <StyledNumberInput
          {...inputProps}
          value={formatNationalInput(country, nationalInput)}
          onChange={handleNumberChange}
          disabled={disabled}
          inputMode="tel"
          $token={token}
          $status={status}
          $height={height}
        />
      </Space.Compact>
      {helpText && (
        <HelpText $token={token} $status={status}>
          {helpText}
        </HelpText>
      )}
    </Wrapper>
  );
};

export default PhoneInput;
